import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (productLink?.href) {
      const url = new URL(productLink.href, window.location.origin);

      if (url.searchParams.has('variant')) {
        return url.toString();
      }

      const selectedVariantId = this.#getSelectedVariantId();
      if (selectedVariantId) {
        url.searchParams.set('variant', selectedVariantId);
      }

      return url.toString();
    }

    /** Product page (e.g. mobile "Select size") — no product-card link */
    const fromDataset = this.dataset.productUrl?.trim();
    if (fromDataset) {
      return new URL(fromDataset, window.location.origin).toString();
    }

    return '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const productPageUrl = this.productPageUrl;
    const requestUrl = this.#buildRequestUrl(productPageUrl);

    // Check if we have cached content for this URL
    let modalContent = this.#cachedContent.get(requestUrl);

    if (!modalContent) {
      // Fetch and cache only the compact quick-add section, not the full PDP.
      await this.fetchProductPage(productPageUrl);
      modalContent = this.#cachedContent.get(requestUrl);
    }

    if (modalContent) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (modalContent.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const modalTitle = dialogComponent.querySelector('[data-quick-add-modal-title]');
    if (modalTitle instanceof HTMLElement) {
      modalTitle.textContent = this.dataset.productTitle || '';
    }

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Builds the Section Rendering API URL while preserving an already selected variant.
   * @param {string} productPageUrl - Product page URL.
   * @returns {string}
   */
  #buildRequestUrl(productPageUrl) {
    if (!productPageUrl) return '';

    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.set('section_id', 'nw-quick-add-modal');
    return url.toString();
  }

  /**
   * Preloads compact quick-add content. Kept public because product cards use it
   * for hover prefetching.
   * @param {string} productPageUrl - Product page URL.
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    const requestUrl = this.#buildRequestUrl(productPageUrl);
    if (!requestUrl) return null;

    const html = await this.fetchQuickAddContent(requestUrl);
    const contentElement = html?.querySelector('[data-quick-add-content]');
    if (contentElement) {
      this.#cachedContent.set(requestUrl, /** @type {Element} */ (contentElement.cloneNode(true)));
    }

    return html;
  }

  /**
   * Fetches compact quick-add content through Shopify's Section Rendering API.
   * @param {string} requestUrl - The section rendering URL.
   * @returns {Promise<Document | null>}
   */
  async fetchQuickAddContent(requestUrl) {
    if (!requestUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(requestUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders compact quick-add content and applies the explicit-size requirement
   * from the source button after the Section Rendering response is mounted.
   * @param {Element} content - The compact quick-add content.
   */
  async updateQuickAddModal(content) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!content || !modalContent) return;

    // Quick-add content belongs to a different product on every modal opening.
    // Recreate it instead of morphing so disabled controls and component state from
    // a previously opened product cannot leak into the next product.
    modalContent.replaceChildren(content);

    const requiresExplicitSizeSelection = this.dataset.requireExplicitSizeSelection === 'true';
    if (requiresExplicitSizeSelection) {
      modalContent.querySelectorAll('variant-picker').forEach((picker) => {
        picker.setAttribute('data-require-explicit-size-selection', 'true');
        picker.querySelectorAll('.nw-variant-card-picker__card input').forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.checked = false;
          input.removeAttribute('checked');
          input.dataset.currentChecked = 'false';
          input.dataset.requiresExplicitSelection = 'true';
        });
      });
      modalContent.querySelectorAll('product-form-component').forEach((form) => {
        form.setAttribute('data-require-explicit-size-selection', 'true');
        const variantId = form.querySelector('input[ref="variantId"]');
        if (variantId instanceof HTMLInputElement) {
          variantId.value = '';
          variantId.disabled = true;
        }
        form.querySelector('add-to-cart-component')?.disable?.();
      });

      const notice = document.createElement('p');
      notice.className = 'quick-add-size-notice';
      notice.setAttribute('role', 'alert');
      notice.textContent = this.dataset.sizeSelectionMessage || 'Selecciona una talla para continuar.';
      modalContent.querySelector('variant-picker')?.insertAdjacentElement('beforebegin', notice);
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductImage);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.nw-product-card-simple-modal__title-link')
    );
    const productTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.nw-product-card-simple-modal__title-link')
    );
    const modalTitle = this.querySelector('[data-quick-add-modal-title]');

    if (!anchorElement) return;

    if (productTitle) productTitle.href = anchorElement.href;
    if (modalTitle) modalTitle.textContent = anchorElement.firstElementChild?.textContent || '';
  };

  #updateProductImage = (/** @type {CustomEvent} */ event) => {
    const nextImage = event.detail.data.html?.querySelector('[data-quick-add-product-image]');
    const currentImage = this.querySelector('[data-quick-add-product-image]');

    if (!(nextImage instanceof Element) || !(currentImage instanceof Element)) return;
    morph(currentImage, nextImage);
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
